import {
    authService,
    supabase,
    WorkspaceIntegrationAPI
} from '@common/api/authService';
import Nango from '@nangohq/frontend';
import type { AxiosRequestConfig } from 'axios';
import {
    useCallback, useEffect, useMemo, useState
} from 'react';

// 集成状态枚举
export type IntegrationStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

// 扩展 API 类型，添加 connected_at 字段
export interface WorkspaceIntegration extends WorkspaceIntegrationAPI {
}

// 按平台分组的集成状态 - 直接使用 API 数据结构
export interface WorkspaceIntegrationsState {
    shopify: WorkspaceIntegration | null;
    bigCommerce: WorkspaceIntegration | null;
    googleAds: WorkspaceIntegration | null;
    slack: WorkspaceIntegration | null;
    woocommerce: WorkspaceIntegration | null;
    magento: WorkspaceIntegration | null;
}

// Hook 返回类型
export interface UseWorkspaceIntegrationsReturn {
    integrations: WorkspaceIntegrationsState;
    allIntegrations: WorkspaceIntegration[];
    isLoading: boolean;
    error: Error | null;
    refresh: () => Promise<WorkspaceIntegration[]>;
    isConnected: (platform: string) => boolean;
    getIntegration: (platform: string) => WorkspaceIntegration | null;
    connect: (platform: string, connectionConfig?: any) => Promise<any>;
    disconnect: (platform: string) => Promise<void>;
    hasConnectedIntegrations: boolean;
}

// API 返回的 provider 名称到内部 key 的映射
const PROVIDER_TO_KEY: Record<string, keyof WorkspaceIntegrationsState> = {
    'shopify': 'shopify',
    'google-ads': 'googleAds',
    'slack': 'slack',
    'bigcommerce': 'bigCommerce',
    'woocommerce': 'woocommerce',
    'magento': 'magento',
};

// 平台到 Nango integration ID 的映射
const PLATFORM_TO_NANGO_ID: Record<string, string> = {
    'shopify': 'shopify',
    'google-ads': 'google-ads',
    'slack': 'slack',
    'bigcommerce': 'bigcommerce',
    'woocommerce': 'woocommerce',
    'magento': 'magento',
};

// Google Ads 账户数据类型
export interface GoogleAdsAccount {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    is_manager: string;
}

export interface GoogleAdsCustomerGroup {
    account_id: string;
    name: string;
    currency_code: string;
    timezone: string;
    accounts: GoogleAdsAccount[];
}

/**
 * 从 workspace API 拉取和管理当前 workspace 的集成状态
 */
export const useWorkspaceIntegrations = (workspaceId?: string): UseWorkspaceIntegrationsReturn => {
    const [allIntegrations, setAllIntegrations] = useState<WorkspaceIntegration[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    /**
     * 移除集成
     */
    const removeIntegration = useCallback((provider: string) => {
        setAllIntegrations(prev => prev.filter(i => i.provider !== provider));
    }, []);

    /**
     * 从 API 拉取当前 workspace 的集成列表
     * @param showLoading - 是否显示 loading 状态，默认为 true
     */
    const fetchIntegrations = useCallback(async (showLoading = true): Promise<WorkspaceIntegration[]> => {
        if (!workspaceId) {
            const empty: WorkspaceIntegration[] = [];
            setAllIntegrations(empty);
            return empty;
        }

        if (showLoading) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const response = await authService.getWorkspaceIntegrations({ headers: { 'X-Workspace-Id': workspaceId } });

            // 直接使用 API 返回的数据格式，不做转换
            const integrations: WorkspaceIntegration[] = [];

            // response 已经是 data 对象了（api.ts 自动提取了 response.data.data）
            if (response) {
                Object.entries(response).forEach(([, apiIntegrations]) => {
                    if (Array.isArray(apiIntegrations) && apiIntegrations.length > 0) {
                        apiIntegrations.forEach((apiIntegration: WorkspaceIntegrationAPI) => {
                            // 添加 connected_at 字段（从 created_at 获取）
                            integrations.push({...apiIntegration,});
                        });
                    }
                });
            }

            setAllIntegrations(integrations);
            return integrations;
        } catch (err: any) {
            console.error('Error fetching workspace integrations:', err);
            setError(err);
            setAllIntegrations([]);
            return [];
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    }, [workspaceId]);

    // 初始化和 workspace 变化时拉取数据
    useEffect(() => {
        fetchIntegrations();
    }, [fetchIntegrations]);


    /**
     * 验证集成是否创建成功
     * 通过直接查询 workspace_integrations 表检查状态
     * @param provider - 集成平台 (如 'shopify', 'google-ads', 'slack', 'bigcommerce')
     * @param connectionId - Nango 连接 ID (可选，Shopify 不需要)
     */
    const checkIntegrationCreated = useCallback(async (provider: string, connectionId?: string): Promise<void> => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        // 转换 provider 到 platform：小写转大写，中划线转下划线
        // 例如: 'google-ads' -> 'GOOGLE_ADS', 'shopify' -> 'SHOPIFY'
        const platform = provider.toUpperCase().replace(/-/g, '_');

        const maxAttempts = 3;

        for (let i = 0; i < maxAttempts; i++) {
            // 等待 1 秒
            await new Promise(resolve => setTimeout(resolve, 1000));

            // 构建 Supabase 查询
            let query = supabase
                .from('workspace_integrations')
                .select('*')
                .eq('workspace_id', workspaceId)
                .eq('platform', platform);

            // 如果有 connectionId，则添加 nango_connection_id 过滤条件
            if (connectionId) {
                query = query.eq('nango_connection_id', connectionId);
            }

            const { data, error } = await query
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error checking integration status:', error);
                continue;
            }

            // 找到记录，检查状态
            if (data) {
                if (data.status === 'ACTIVE') {
                    await fetchIntegrations(false); // 刷新数据
                    return; // 成功则立即返回
                } else {
                    // 状态为非 ACTIVE，抛出错误信息
                    const errorMsg = data.error_message || `Integration ${provider} failed with status: ${data.status}`;
                    throw new Error(errorMsg);
                }
            }
        }

        // 3 次检查都失败
        throw new Error(`Failed to create ${provider} integration`);
    }, [workspaceId]);

    /**
     * 将集成列表按平台分组，只返回每个平台的第一个已连接集成
     */
    const integrations: WorkspaceIntegrationsState = useMemo(() => {
        const result: WorkspaceIntegrationsState = {
            shopify: null,
            bigCommerce: null,
            googleAds: null,
            slack: null,
            woocommerce: null,
            magento: null,
        };

        allIntegrations.forEach((integration) => {
            const key = PROVIDER_TO_KEY[integration.provider];
            if (key && integration.connected && !result[key]) {
                result[key] = integration;
            }
        });

        return result;
    }, [allIntegrations]);

    /**
     * 检查指定平台是否已连接
     */
    const isConnected = useCallback((platform: string): boolean => {
        const normalizedProvider = platform.toLowerCase().replace(/\s+/g, '-').replace('_', '-');
        return allIntegrations.some(
            (i) => i.provider === normalizedProvider && i.connected
        );
    }, [allIntegrations]);

    /**
     * 获取指定平台的集成信息
     */
    const getIntegration = useCallback((platform: string): WorkspaceIntegration | null => {
        const normalizedProvider = platform.toLowerCase().replace(/\s+/g, '-').replace('_', '-');
        return (
            allIntegrations.find(
                (i) => i.provider === normalizedProvider && i.connected
            ) || null
        );
    }, [allIntegrations]);

    /**
     * 检查是否有任何已连接的集成（排除 slack）
     */
    const hasConnectedIntegrations = useMemo(() => {
        return allIntegrations.some(
            (integration) => integration.connected && integration.provider !== 'slack'
        );
    }, [allIntegrations]);

    /**
     * 连接平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     * @param connectionConfig - 连接配置（可选，如 store_domain, redirect_url 等）
     */
    const connect = useCallback(async (platform: string, connectionConfig?: any) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig: AxiosRequestConfig = { headers: { 'X-Workspace-Id': workspaceId } };

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');

        switch (normalizedProvider) {
            case 'shopify': {
                // Shopify 连接
                const { shop_domain, redirect_url } = connectionConfig || {};
                if (!shop_domain) {
                    throw new Error('shop_domain is required for Shopify connection');
                }

                const res = await authService.connectShopify({
                    shop_domain,
                    redirect_url: redirect_url,
                    axiosRequestConfig
                });

                if (res.authorizationUrl) {
                    // 打开新窗口进行授权
                    window.open(res.authorizationUrl, '_blank', 'width=800,height=600');

                    // 等待授权完成的消息
                    await new Promise<void>((resolve, reject) => {
                        const channel = new BroadcastChannel('connect');
                        const timeout = setTimeout(() => {
                            channel.close();
                            reject(new Error('Shopify authorization timeout'));
                        }, 300000); // 5 分钟超时

                        channel.onmessage = async (e) => {
                            if (e.data.type === 'SUCCESS') {
                                console.log('connect 成功', e.data);
                                clearTimeout(timeout);
                                channel.close();
                                // 验证集成是否创建成功
                                await checkIntegrationCreated('shopify');
                                resolve();
                            } else if (e.data.type === 'ERROR') {
                                clearTimeout(timeout);
                                channel.close();
                                reject(new Error(e.data.message || 'Shopify authorization failed'));
                            }
                        };
                    });

                    return { success: true };
                } else {
                    throw new Error('Shopify store is not found. Please check the store url.');
                }
            }

            case 'google-ads': {
                // Google Ads 连接
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'google-ads',
                    axiosRequestConfig
                });
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const result = await nango.auth('google-ads');
                console.log("result", result);

                // Only proceed if we have a connection ID
                if (result?.connectionId) {
                    // 验证集成是否创建成功
                    await checkIntegrationCreated('google-ads', result.connectionId);
                }

                return result;
            }

            case 'slack': {
                // Slack 连接
                const tokenBody = await authService.getNangoConnectToken({
                    integration_id: 'slack',
                    axiosRequestConfig
                });
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;

                if (!nangoToken) throw new Error('Failed to retrieve connect token');

                const nango = new Nango({ connectSessionToken: nangoToken });
                const result = await nango.auth('slack');

                if (result?.connectionId) {
                    // 验证集成是否创建成功
                    await checkIntegrationCreated('slack', result.connectionId);
                }

                return result;
            }

            case 'bigcommerce': {
                // BigCommerce 连接
                const requestBody = {
                    integration_id: 'bigcommerce',
                    ...connectionConfig,
                    axiosRequestConfig
                };

                const tokenBody = await authService.getNangoConnectToken(requestBody);

                // 处理重定向（App 安装）
                if (tokenBody.action === 'REDIRECT' && tokenBody.url) {
                    window.location.href = tokenBody.url;
                    return { action: 'REDIRECT' };
                }

                // 处理 Token 响应（连接）
                const nangoToken = tokenBody.data?.token || tokenBody.result?.token || tokenBody.token;
                if (!nangoToken) throw new Error('未获取到 Token');

                const nango = new Nango({ connectSessionToken: nangoToken });

                // 使用 Connect Session Token 时，不能手动指定 connectionId
                // Nango 会自动生成唯一的 connection ID
                const authParams = {
                    params: {
                        storeHash: connectionConfig?.store_hash || 'zooye8wllz',
                        accountUuid: connectionConfig?.accountUuid || '7a74c338-861e-4087-9284-f79a8832440a'
                    }
                };

                // @ts-ignore
                const result = await nango.auth('bigcommerce', undefined, authParams as any);

                if (result?.connectionId) {
                    // 验证集成是否创建成功
                    await checkIntegrationCreated('bigcommerce', result.connectionId);
                }

                return result;
            }

            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }
    }, [workspaceId, checkIntegrationCreated]);

    /**
     * 断开平台集成
     * @param platform - 平台名称（如 'shopify', 'google-ads', 'slack'）
     */
    const disconnect = useCallback(async (platform: string) => {
        if (!workspaceId) {
            throw new Error('Workspace ID is required');
        }

        const axiosRequestConfig: AxiosRequestConfig = { headers: { 'X-Workspace-Id': workspaceId } };

        // 标准化平台名称：同时处理空格和下划线
        const normalizedProvider = platform.toLowerCase().replace(/[\s_]+/g, '-');

        switch (normalizedProvider) {
            case 'shopify': {
                // Shopify 断开 - 从 mappings 中获取店铺信息
                const shopifyIntegration = integrations.shopify;
                const firstMapping = shopifyIntegration?.mappings?.[0];
                const shopNameOrDomain = firstMapping?.account_name || firstMapping?.connection_id;

                if (!shopNameOrDomain) {
                    throw new Error('Cannot disconnect Shopify: no shop domain found');
                }

                const shopDomain = shopNameOrDomain.includes('.myshopify.com')
                    ? shopNameOrDomain
                    : `${shopNameOrDomain}.myshopify.com`;

                await authService.disconnectShopify({
                    shop_domain: shopDomain,
                    axiosRequestConfig
                });
                break;
            }

            case 'google-ads':
            case 'slack':
            case 'bigcommerce': {
                // Nango 集成的断开
                await authService.unbindNangoIntegration({
                    integration_id: PLATFORM_TO_NANGO_ID[normalizedProvider] || normalizedProvider,
                    axiosRequestConfig
                });
                break;
            }

            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }

        // 直接更新集成状态
        removeIntegration(normalizedProvider);
    }, [workspaceId, integrations, removeIntegration]);

    return {
        integrations,
        allIntegrations,
        isLoading,
        error,
        refresh: fetchIntegrations,
        isConnected,
        getIntegration,
        connect,
        disconnect,
        hasConnectedIntegrations,
    };
};

/**
 * 工具函数：将 UI 中的平台名称转换为 provider 格式
 */
export const normalizePlatformName = (name: string): string => {
    return name.toLowerCase().replace(/\s+/g, '-');
};
